import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';

import Button, { ButtonType } from 'inquiry/components/Button';
import { PhoneInput } from 'inquiry/components/FormControl';
import { CountryCode, POSTAL_CODE_PRIMARY_COUNTRIES } from 'lib/constants/locales';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import FormControlField from 'lib/interfaces/FormControlField';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { AddressAutocompleteMethod, InputField } from 'lib/interfaces/verificationConfig';
import {
  isMoreThanAYearAgo,
  isRequired,
  isRequiredDate,
  isValidDate,
  isWithinLastCentury,
  matchesRegexp,
  withMessage,
} from 'lib/utils/formValidation';
import isValidPhone from 'lib/utils/formValidation/isValidPhone';
import AddressInputSection from 'verify/components/AddressInputSection';
import FormGroup from 'verify/components/FormGroup';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import SsnFullField from 'verify/components/SsnFullField';
import ThemedFormDateInput from 'verify/components/ThemedFormDateInput';
import ThemedFormEmailAddressInput from 'verify/components/ThemedFormEmailAddressInput';
import ThemedFormNameInput from 'verify/components/ThemedFormNameInput';
import SsnLast4Field from 'verify/components/ThemedFormSsnLast4Input';
import { RootState } from 'verify/redux/store';

const validate = {
  firstName: [withMessage('Please enter your first name', isRequired)],
  lastName: [withMessage('Please enter your last name', isRequired)],
  birthdate: [
    withMessage('Your legal birth date is required', isRequiredDate),
    withMessage('Enter a valid date', isValidDate),
    withMessage('Birthdate must be within last century', isWithinLastCentury),
    withMessage('Birthdate must be more than a year ago', isMoreThanAYearAgo),
  ],
  phoneNumber: [
    withMessage('The phone number you entered is not valid', isValidPhone),
    withMessage('Enter your phone number', isRequired),
  ],
  emailAddress: [
    withMessage('Please enter your email address', isRequired),
    withMessage('The email you entered is not valid', matchesRegexp(EMAIL_ADDRESS_REGEX)),
  ],
};

interface FormDatabaseProps {
  changeFormValue: (form: string, field: string, value: string) => void;
  selectedCountryCode: CountryCode;
  className?: string;
  latLng: google.maps.LatLngLiteral;
  lockedAttributes?: DatabaseFormData;
  inputFields: InputField[];
  isProcessing: boolean;
  addressAutocompleteMethod: AddressAutocompleteMethod;
}

function mapInputNameToField(
  inputField: string,
  isAddressSet: boolean,
  t: TFunction,
  props: FormDatabaseProps & InjectedFormProps<DatabaseFormData, FormDatabaseProps>,
) {
  switch (inputField) {
    case InputField.Name:
      return (
        <ThemedFormNameInput
          key={InputField.Name}
          disabledNameFirst={
            props.submitting || props.lockedAttributes?.[PiiAttributes.NameFirst] != null
          }
          disabledNameLast={
            props.submitting || props.lockedAttributes?.[PiiAttributes.NameLast] != null
          }
          validate={validate}
        />
      );
    case InputField.Birthdate:
      return (
        <FormGroup key={InputField.Birthdate}>
          <Field
            name={PiiAttributes.Birthdate}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            component={ThemedFormDateInput}
            disabled={props.submitting || props.lockedAttributes?.[PiiAttributes.Birthdate]}
            t={t}
            type='text'
            validate={validate.birthdate}
          />
        </FormGroup>
      );
    case InputField.PhoneNumber:
      return (
        <FormGroup key={InputField.PhoneNumber}>
          <label>{t('phone-number.form.label')}</label>
          <FormControlField
            name={PiiAttributes.PhoneNumber}
            disabled={props.submitting || props.lockedAttributes?.[PiiAttributes.PhoneNumber]}
            t={t}
            validate={validate.phoneNumber}
            component={PhoneInput}
            type='tel'
            defaultCountry={props.selectedCountryCode}
          />
        </FormGroup>
      );
    case InputField.IdentificationNumberFull9:
      return (
        <FormGroup key={InputField.IdentificationNumberFull9}>
          <SsnFullField
            disabled={
              props.submitting ||
              props.lockedAttributes?.[PiiAttributes.IdentificationNumber] != null
            }
            name={PiiAttributes.IdentificationNumber}
            countryCode={props.selectedCountryCode}
          />
        </FormGroup>
      );
    case InputField.IdentificationNumberLast4:
      return (
        <FormGroup key={InputField.IdentificationNumberLast4}>
          <SsnLast4Field
            disabled={
              props.submitting ||
              props.lockedAttributes?.[PiiAttributes.IdentificationNumber] != null
            }
            name={PiiAttributes.IdentificationNumber}
          />
        </FormGroup>
      );
    case InputField.Address:
      return (
        <AddressInputSection
          key={InputField.Address}
          boundChangeFormValue={(field: string, value: string) =>
            props.changeFormValue(props.form, field, value)
          }
          selectedCountryCode={props.selectedCountryCode}
          disabled={props.submitting}
          forceOpen={props.submitFailed || isAddressSet}
          latLng={props.latLng}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lockedAttributes={props.lockedAttributes}
          t={t}
          addressAutocompleteMethod={props.addressAutocompleteMethod}
        />
      );
    case InputField.EmailAddress:
      return (
        <ThemedFormEmailAddressInput
          key={InputField.EmailAddress}
          disabled={
            props.submitting || props.lockedAttributes?.[PiiAttributes.EmailAddress] != null
          }
          validate={validate}
        />
      );
  }
}

function FormDatabaseComponent(
  props: FormDatabaseProps & InjectedFormProps<DatabaseFormData, FormDatabaseProps>,
) {
  const formValues: Partial<DatabaseFormData> = useSelector((state: RootState) => {
    const selector = formValueSelector(props.form);

    return {
      [PiiAttributes.AddressStreet1]: selector(state, PiiAttributes.AddressStreet1) || '',
      [PiiAttributes.AddressStreet2]: selector(state, PiiAttributes.AddressStreet2) || '',
      [PiiAttributes.AddressCity]: selector(state, PiiAttributes.AddressCity) || '',
      [PiiAttributes.AddressSubdivision]: selector(state, PiiAttributes.AddressSubdivision) || '',
      [PiiAttributes.AddressPostalCode]: selector(state, PiiAttributes.AddressPostalCode) || '',
    };
  });

  const isAddressSet = (
    [
      PiiAttributes.AddressStreet2,
      PiiAttributes.AddressCity,
      PiiAttributes.AddressSubdivision,
      POSTAL_CODE_PRIMARY_COUNTRIES.includes(props.selectedCountryCode)
        ? PiiAttributes.AddressStreet1
        : PiiAttributes.AddressPostalCode,
    ] as const
  ).some((attributeName) => formValues[attributeName]);
  const [t] = useTranslation();

  const formFields = props.inputFields.map((inputField) =>
    mapInputNameToField(inputField, isAddressSet, t, props),
  );

  return (
    <form onSubmit={props.handleSubmit}>
      {formFields}
      <PositionableButtonContainer>
        <Button
          type='submit'
          buttonType={ButtonType.Primary}
          disabled={props.pristine || props.submitting || props.isProcessing}
          isPending={props.submitting || props.isProcessing}
        >
          {t('database.form.btn-submit')}
        </Button>
      </PositionableButtonContainer>
    </form>
  );
}

export interface DatabaseFormData {
  [PiiAttributes.NameFirst]?: string;
  [PiiAttributes.NameMiddle]?: string;
  [PiiAttributes.NameLast]?: string;
  [PiiAttributes.Birthdate]?: string;
  [PiiAttributes.PhoneNumber]?: string;
  [PiiAttributes.AddressStreet1]?: string;
  [PiiAttributes.AddressStreet2]?: string;
  [PiiAttributes.AddressCity]?: string;
  [PiiAttributes.AddressSubdivision]?: string;
  [PiiAttributes.AddressPostalCode]?: string;
  [PiiAttributes.IdentificationNumber]?: string;
  [PiiAttributes.EmailAddress]?: string;
}

export const FORM_NAME = 'database';

export default reduxForm<DatabaseFormData, FormDatabaseProps>({
  form: FORM_NAME,
  // We want the form to not get unregistered when retrying. This is due to a
  // race condition where we route the user to the next step BEFORE the submit
  // promise completes. Order of actions goes initialize -> submit -> fail ->
  // initialize (next form which is the same form; no-op) -> destroy (
  // original form). This causes form validations to break, allowing users to
  // submit blank SSNs, etc., which causes problems.
  destroyOnUnmount: false,
})(FormDatabaseComponent);
